import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandedListTab from '../ExpandedListTab';
import { clientApplicationsType } from '../../types';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    ALL_CLIENT_APPLICATIONS_QUERY_NAME,
    UPDATE_CLIENT_APPLICATIONS,
} from '../../graphql/settings';
import ClientApplications from './ClientApplications';
import useExtendedState from '../../hooks/useExtendedState';
import {
    createTemporaryId,
    isTemporaryId,
} from '../../utils/tableRecords';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';

ClientApplicationsDetailTab.propTypes = {
    clientApplications: clientApplicationsType.isRequired,
};

const mapRawClientApplications = (clientApplications) => clientApplications.map((clientApplication) => ({
    id: clientApplication.id,
    applicationId: clientApplication.applicationId,
    description: clientApplication.description,
    active: clientApplication.active,
    isValid: true,
}));

function ClientApplicationsDetailTab(props) {
    const {
        clientApplications,
    } = props;
    const { t } = useTranslation();
    const currentClientApplicationsState = useExtendedState(mapRawClientApplications(clientApplications));
    useEffectOnUpdate(() => {
        currentClientApplicationsState.changeInitialState(mapRawClientApplications(clientApplications));
        currentClientApplicationsState.reset();
    }, [clientApplications]);
    const [doReset, setDoReset] = useState(false);
    const [backendChanged, setBackendChanged] = useState(false);
    const onClientApplicationCreate = (applicationId, description, active) => {
        currentClientApplicationsState.setValue([...currentClientApplicationsState.value, {
            id: createTemporaryId(),
            applicationId,
            description,
            active,
        }]);
        setBackendChanged(false);
        setDoReset(false);
    };
    const onClientApplicationDelete = (id) => {
        const newClientApplications = currentClientApplicationsState.value.filter((currentClientApplication) => currentClientApplication.id !== id);
        currentClientApplicationsState.setValue(newClientApplications);
        setBackendChanged(false);
        setDoReset(false);
    };
    const onClientApplicationValidated = (clientApplicationToChange) => (newDataToChange, isValid) => {
        const newClientApplications = currentClientApplicationsState.value.map((clientApplication) => {
            let result = clientApplication;
            if (clientApplication === clientApplicationToChange) {
                result = {
                    id: clientApplication.id,
                    applicationId: newDataToChange.applicationId,
                    description: newDataToChange.description,
                    active: newDataToChange.active,
                    isValid,
                };
            }
            return result;
        });
        currentClientApplicationsState.setValue(newClientApplications);
        setBackendChanged(false);
        setDoReset(false);
    };
    const onReset = () => {
        currentClientApplicationsState.reset();
        setDoReset(true);
    };
    const isInvalidClientApplication = () => currentClientApplicationsState.value.some((currentClientApplication) => currentClientApplication.isValid === false);
    const isApplyChangesDisabled = !currentClientApplicationsState.isChanged || isInvalidClientApplication();
    const updateClientApplicationsExtendedMutation = useExtendedMutation(UPDATE_CLIENT_APPLICATIONS, {
        variables: {
            clientApplications: currentClientApplicationsState.value.map((currentClientApplication) => ({
                id: isTemporaryId(currentClientApplication.id) ? null : currentClientApplication.id,
                applicationId: currentClientApplication.applicationId,
                description: currentClientApplication.description,
                active: currentClientApplication.active,
            })),
        },
        successCallback: () => {
            setBackendChanged(true);
            setDoReset(false);
        },
        successMessageOptions: { key: 'settings.client-applications.notification.client-applications-update-success', dataProperty: 'updateClientApplications' },
        failureMessageOptions: { key: 'settings.client-applications.notification.client-applications-update-failure' },
        refetchQueries: [ALL_CLIENT_APPLICATIONS_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="settings-detail-tab"
        >
            <div className="rich-list-row-expanded--fields">
                <ClientApplications
                    clientApplications={currentClientApplicationsState.value}
                    onClientApplicationCreate={onClientApplicationCreate}
                    onClientApplicationDelete={onClientApplicationDelete}
                    onClientApplicationValidated={onClientApplicationValidated}
                    onReset={onReset}
                    isChanged={currentClientApplicationsState.isChanged}
                    backendChanged={backendChanged}
                    doReset={doReset}
                />
            </div>
            <div className="rich-list-row-expanded--buttons">
                <div className="rich-list-row-expanded--buttons--left">
                    <ApplyChangesButton
                        confirmationMessage={t('settings.client-applications.confirm-update-client-applications')}
                        extendedMutation={updateClientApplicationsExtendedMutation}
                        disabled={isApplyChangesDisabled}
                        showConfirmationDialog
                    />
                </div>
            </div>
        </ExpandedListTab>
    );
}

export default ClientApplicationsDetailTab;
